/** VS Code Dark+ JSON token colors */
export const JSON_COLORS = {
    key: "#9cdcfe",
    string: "#ce9178",
    number: "#b5cea8",
    boolean: "#569cd6",
    null: "#569cd6",
    bracket: "#ffd700",
    punctuation: "#d4d4d4",
    default: "#d4d4d4",
} as const;

export interface FoldRegion {
    startLine: number;
    endLine: number;
    open: "{" | "[";
    close: "}" | "]";
}

export interface SearchMatch {
    start: number;
    end: number;
}

export interface JsonToken {
    start: number;
    end: number;
    color: string;
}

export const stringifyConfig = (config: unknown): string => {
    try {
        return JSON.stringify(config, null, 2);
    } catch {
        return "Unable to serialize configuration.";
    }
};

const escapeHtml = (text: string): string =>
    text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

export const getJsonTokens = (text: string): JsonToken[] => {
    const tokenPattern =
        /"(?:\\.|[^"\\])*"(\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?|[{}\[\]:,]/g;

    const tokens: JsonToken[] = [];
    let match: RegExpExecArray | null;

    while ((match = tokenPattern.exec(text)) !== null) {
        const token = match[0];
        const hasKeyColon = Boolean(match[1]);
        let color: string = JSON_COLORS.punctuation;

        if (token.startsWith('"')) {
            color = hasKeyColon ? JSON_COLORS.key : JSON_COLORS.string;
        } else if (token === "true" || token === "false") {
            color = JSON_COLORS.boolean;
        } else if (token === "null") {
            color = JSON_COLORS.null;
        } else if (/^-?\d/.test(token)) {
            color = JSON_COLORS.number;
        } else if (token === "{" || token === "}" || token === "[" || token === "]") {
            color = JSON_COLORS.bracket;
        }

        tokens.push({ start: match.index, end: match.index + token.length, color });
    }

    return tokens;
};

export const findSearchMatches = (
    text: string,
    query: string,
    caseSensitive: boolean
): SearchMatch[] => {
    if (!query) return [];

    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const flags = caseSensitive ? "g" : "gi";
    const regex = new RegExp(escaped, flags);
    const matches: SearchMatch[] = [];
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
        matches.push({ start: match.index, end: match.index + match[0].length });
        if (match[0].length === 0) {
            regex.lastIndex += 1;
        }
    }

    return matches;
};

export const getLineForPosition = (text: string, position: number): number =>
    text.slice(0, Math.max(0, position)).split("\n").length;

export const getCollapsedLinesWithLineExpanded = (
    collapsedLines: ReadonlySet<number>,
    regions: FoldRegion[],
    line: number
): Set<number> => {
    const next = new Set(collapsedLines);
    regions.forEach((region) => {
        if (line > region.startLine && line <= region.endLine) {
            next.delete(region.startLine);
        }
    });
    return next;
};

export const mapFullPositionToDisplay = (
    fullText: string,
    collapsedLines: ReadonlySet<number>,
    regions: FoldRegion[],
    fullPosition: number
): number => {
    if (collapsedLines.size === 0) {
        return fullPosition;
    }

    const lines = fullText.split("\n");
    const hiddenLines = new Set<number>();
    const suffixByLine = new Map<number, string>();

    regions.forEach((region) => {
        if (!collapsedLines.has(region.startLine)) return;
        for (let line = region.startLine + 1; line <= region.endLine; line += 1) {
            hiddenLines.add(line);
        }
        suffixByLine.set(region.startLine, ` ... ${region.close}`);
    });

    let displayOffset = 0;
    let fullOffset = 0;

    for (let index = 0; index < lines.length; index += 1) {
        const lineNumber = index + 1;
        const line = lines[index];

        if (hiddenLines.has(lineNumber)) {
            fullOffset += line.length + 1;
            continue;
        }

        const suffix = suffixByLine.get(lineNumber) ?? "";
        const displayLine = line + suffix;
        const lineEnd = fullOffset + line.length;

        if (fullPosition >= fullOffset && fullPosition <= lineEnd) {
            return displayOffset + (fullPosition - fullOffset);
        }

        displayOffset += displayLine.length + 1;
        fullOffset += line.length + 1;
    }

    return displayOffset;
};

export const mapSearchMatchesToDisplay = (
    fullText: string,
    collapsedLines: ReadonlySet<number>,
    regions: FoldRegion[],
    matches: SearchMatch[]
): SearchMatch[] =>
    matches
        .map((match) => ({
            start: mapFullPositionToDisplay(fullText, collapsedLines, regions, match.start),
            end: mapFullPositionToDisplay(fullText, collapsedLines, regions, match.end),
        }))
        .filter((match) => match.start < match.end);

export const buildHighlightedHtmlFromMatches = (
    text: string,
    displayMatches: SearchMatch[],
    activeMatchIndex = 0
): string => {
    if (!text) return "";

    const tokens = getJsonTokens(text);
    const breakpoints = new Set<number>([0, text.length]);

    tokens.forEach((token) => {
        breakpoints.add(token.start);
        breakpoints.add(token.end);
    });
    displayMatches.forEach((match) => {
        breakpoints.add(match.start);
        breakpoints.add(match.end);
    });

    const points = [...breakpoints].sort((a, b) => a - b);
    const activeMatch = displayMatches[activeMatchIndex];
    let html = "";

    for (let index = 0; index < points.length - 1; index += 1) {
        const start = points[index];
        const end = points[index + 1];
        if (start >= end) continue;

        const chunk = escapeHtml(text.slice(start, end));
        if (!chunk) continue;

        const token = tokens.find((item) => item.start <= start && item.end >= end);
        const color = token?.color ?? JSON_COLORS.default;
        const inSearch = displayMatches.some(
            (match) => match.start <= start && match.end >= end
        );
        const isActive =
            activeMatch && activeMatch.start <= start && activeMatch.end >= end;

        let segment = `<span style="color:${color}">${chunk}</span>`;

        if (inSearch) {
            const background = isActive ? "#613214" : "#484848";
            segment = `<span style="background-color:${background}; border-radius:2px; color:${color}">${chunk}</span>`;
        }

        html += segment;
    }

    return html;
};

export const buildHighlightedHtmlWithCollapsedPlaceholders = (
    text: string,
    placeholderRanges: CollapsedPlaceholderRange[],
    displayMatches: SearchMatch[] = [],
    activeMatchIndex = 0
): string => {
    if (!text) return "";

    const tokens = getJsonTokens(text);
    const breakpoints = new Set<number>([0, text.length]);

    tokens.forEach((token) => {
        breakpoints.add(token.start);
        breakpoints.add(token.end);
    });
    placeholderRanges.forEach((range) => {
        breakpoints.add(range.start);
        breakpoints.add(range.end);
    });
    displayMatches.forEach((match) => {
        breakpoints.add(match.start);
        breakpoints.add(match.end);
    });

    const points = [...breakpoints].sort((a, b) => a - b);
    const activeMatch = displayMatches[activeMatchIndex];
    let html = "";

    for (let index = 0; index < points.length - 1; index += 1) {
        const start = points[index];
        const end = points[index + 1];
        if (start >= end) continue;

        const chunk = escapeHtml(text.slice(start, end));
        if (!chunk) continue;

        const token = tokens.find((item) => item.start <= start && item.end >= end);
        const color = token?.color ?? JSON_COLORS.default;
        const inSearch = displayMatches.some(
            (match) => match.start <= start && match.end >= end
        );
        const isActive =
            activeMatch && activeMatch.start <= start && activeMatch.end >= end;
        const inCollapsedPlaceholder = placeholderRanges.some(
            (range) => range.start <= start && range.end >= end
        );

        let segment = `<span style="color:${color}">${chunk}</span>`;

        if (inSearch) {
            const background = isActive ? "#613214" : "#484848";
            segment = `<span style="background-color:${background}; border-radius:2px; color:${color}">${chunk}</span>`;
        } else if (inCollapsedPlaceholder) {
            segment = `<span class="screen-config-json-collapsed-placeholder" style="color:${color}">${chunk}</span>`;
        }

        html += segment;
    }

    return html;
};

export const buildDisplayText = (
    fullText: string,
    collapsedLines: ReadonlySet<number>,
    regions: FoldRegion[]
): string => {
    if (collapsedLines.size === 0) {
        return fullText;
    }

    const lines = fullText.split("\n");
    const hiddenLines = new Set<number>();
    const suffixByLine = new Map<number, string>();

    regions.forEach((region) => {
        if (!collapsedLines.has(region.startLine)) return;

        for (let line = region.startLine + 1; line <= region.endLine; line += 1) {
            hiddenLines.add(line);
        }

        suffixByLine.set(region.startLine, ` ... ${region.close}`);
    });

    return lines
        .map((line, index) => {
            const lineNumber = index + 1;
            if (hiddenLines.has(lineNumber)) return null;
            return line + (suffixByLine.get(lineNumber) ?? "");
        })
        .filter((line): line is string => line !== null)
        .join("\n");
};

export interface CollapsedPlaceholderRange {
    start: number;
    end: number;
    startLine: number;
}

export const getCollapsedPlaceholderRanges = (
    fullText: string,
    collapsedLines: ReadonlySet<number>,
    regions: FoldRegion[]
): CollapsedPlaceholderRange[] => {
    if (collapsedLines.size === 0) return [];

    const lines = fullText.split("\n");
    const hiddenLines = new Set<number>();
    const suffixByLine = new Map<number, string>();

    regions.forEach((region) => {
        if (!collapsedLines.has(region.startLine)) return;
        for (let line = region.startLine + 1; line <= region.endLine; line += 1) {
            hiddenLines.add(line);
        }
        suffixByLine.set(region.startLine, ` ... ${region.close}`);
    });

    const ranges: CollapsedPlaceholderRange[] = [];
    let displayOffset = 0;

    for (let index = 0; index < lines.length; index += 1) {
        const lineNumber = index + 1;
        const line = lines[index];

        if (hiddenLines.has(lineNumber)) {
            continue;
        }

        const suffix = suffixByLine.get(lineNumber);
        const displayLine = line + (suffix ?? "");

        if (suffix) {
            const openIndex = Math.max(line.lastIndexOf("{"), line.lastIndexOf("["));
            const rangeStart =
                openIndex >= 0 ? displayOffset + openIndex : displayOffset + line.length;
            const rangeEnd = displayOffset + displayLine.length;

            ranges.push({
                start: rangeStart,
                end: rangeEnd,
                startLine: lineNumber,
            });
        }

        displayOffset += displayLine.length + 1;
    }

    return ranges;
};

export const normalizeCollapsedSelection = (
    selectionStart: number,
    selectionEnd: number,
    placeholderRanges: CollapsedPlaceholderRange[]
): { start: number; end: number } => {
    const start = Math.min(selectionStart, selectionEnd);
    const end = Math.max(selectionStart, selectionEnd);

    if (start === end || placeholderRanges.length === 0) {
        return { start: selectionStart, end: selectionEnd };
    }

    for (const range of placeholderRanges) {
        const startInside = start >= range.start && start <= range.end;
        const endInside = end >= range.start && end <= range.end;
        const spansPlaceholder = start < range.start && end > range.end;

        if (startInside || endInside || spansPlaceholder) {
            return { start: range.start, end: range.end };
        }
    }

    return { start: selectionStart, end: selectionEnd };
};

export const getCollapsedPlaceholderAtPosition = (
    position: number,
    placeholderRanges: CollapsedPlaceholderRange[]
): CollapsedPlaceholderRange | undefined =>
    placeholderRanges.find((range) => position >= range.start && position <= range.end);

export const buildHighlightedHtml = (
    text: string,
    searchQuery = "",
    caseSensitive = false,
    activeMatchIndex = 0
): string => {
    const matches = findSearchMatches(text, searchQuery, caseSensitive);
    return buildHighlightedHtmlFromMatches(text, matches, activeMatchIndex);
};

export const findFoldRegions = (text: string): FoldRegion[] => {
    const lineStarts: number[] = [0];
    for (let index = 0; index < text.length; index += 1) {
        if (text[index] === "\n") {
            lineStarts.push(index + 1);
        }
    }

    const posToLine = (pos: number): number => {
        for (let index = lineStarts.length - 1; index >= 0; index -= 1) {
            if (pos >= lineStarts[index]) {
                return index + 1;
            }
        }
        return 1;
    };

    const stack: { open: "{" | "["; line: number }[] = [];
    const regions: FoldRegion[] = [];
    let inString = false;
    let escaped = false;

    for (let index = 0; index < text.length; index += 1) {
        const char = text[index];

        if (inString) {
            if (escaped) {
                escaped = false;
            } else if (char === "\\") {
                escaped = true;
            } else if (char === '"') {
                inString = false;
            }
            continue;
        }

        if (char === '"') {
            inString = true;
            continue;
        }

        if (char === "{" || char === "[") {
            stack.push({ open: char, line: posToLine(index) });
            continue;
        }

        if (char === "}" || char === "]") {
            const open = stack.pop();
            if (
                open &&
                ((open.open === "{" && char === "}") || (open.open === "[" && char === "]"))
            ) {
                const endLine = posToLine(index);
                if (endLine > open.line) {
                    regions.push({
                        startLine: open.line,
                        endLine,
                        open: open.open,
                        close: char,
                    });
                }
            }
        }
    }

    return regions;
};
